// ═══════════════════════════════════════════════════════════════
// Weboldal megőrzése mellékletként: képként (PNG), PDF-ként vagy HTML-ként
//
// A szerver már kitisztított tartalmat ad (nincs szkript, eseménykezelő, űrlap, külső
// erőforrás). Itt:
//   1. egy SZKRIPT NÉLKÜLI, rejtett iframe-be töltjük (sandbox="allow-same-origin",
//      allow-scripts NINCS), hogy lemérjük a magasságát;
//   2. a dokumentumot SVG <foreignObject>-be tesszük és <img>-ként rajzoljuk canvasra –
//      képként betöltött SVG-ben semmi nem futhat és semmi külső nem töltődik be;
//   3. a canvasból PNG, illetve A4-es oldalakra vágott PDF (jsPDF) készül.
// A magyar ékezetek képként kerülnek a PDF-be, így minden karakter helyesen látszik.
// ═══════════════════════════════════════════════════════════════

const PAGE_WIDTH = 900;
const MAX_HEIGHT = 15000; // ennél hosszabb oldalt levágunk (a HTML-változat teljes marad)

const PAGE_CSS = `
  *{box-sizing:border-box}
  body{margin:0;padding:24px 28px;background:#fff;color:#1e293b;font:14px/1.5 -apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;width:${PAGE_WIDTH}px;word-wrap:break-word}
  .kozad-src{border-bottom:2px solid #1d4ed8;margin:0 0 16px;padding:0 0 10px;color:#475569;font-size:12px}
  .kozad-src b{display:block;color:#0f172a;font-size:16px;margin-bottom:2px}
  h1,h2,h3,h4{color:#0f172a;margin:14px 0 6px;line-height:1.25} h1{font-size:20px} h2{font-size:18px} h3{font-size:16px} h4{font-size:15px}
  p{margin:6px 0} ul,ol{margin:6px 0 6px 22px;padding:0}
  a[href]{color:#1d4ed8;text-decoration:underline} a:not([href]){color:inherit;text-decoration:none}
  table{border-collapse:collapse;width:100%;margin:8px 0} td,th{border:1px solid #cbd5e1;padding:4px 6px;vertical-align:top;text-align:left}
  div,section,article{margin:2px 0}
`;

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

/** A mentendő teljes HTML-dokumentum (forrás-fejléccel). */
export function buildCapturedDocument(opts: { html: string; title: string; sourceUrl: string; fetchedAt?: string }) {
  const when = new Date(opts.fetchedAt || Date.now()).toLocaleString('hu-HU');
  return `<!DOCTYPE html><html lang="hu"><head><meta charset="utf-8"><title>${esc(opts.title || 'Mentett weboldal')}</title><style>${PAGE_CSS}</style></head>`
    + `<body><div class="kozad-src"><b>${esc(opts.title || 'Mentett weboldal')}</b>Forrás: ${esc(opts.sourceUrl)} · mentve: ${esc(when)}</div>${opts.html}</body></html>`;
}

/** Rejtett, szkript nélküli iframe a méréshez; a hívó felelős az eltávolításáért. */
async function loadIntoInertFrame(docHtml: string): Promise<HTMLIFrameElement> {
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-same-origin'); // szkript NEM futhat
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = `position:fixed;left:-10000px;top:0;width:${PAGE_WIDTH}px;height:50px;border:0;visibility:hidden;`;
  document.body.appendChild(frame);
  await new Promise<void>((resolve) => {
    frame.onload = () => resolve();
    frame.srcdoc = docHtml;
  });
  return frame;
}

/** A dokumentum képe canvasra rajzolva. */
export async function renderDocumentToCanvas(docHtml: string): Promise<{ canvas: HTMLCanvasElement; truncated: boolean }> {
  const frame = await loadIntoInertFrame(docHtml);
  try {
    const doc = frame.contentDocument;
    if (!doc) throw new Error('A tartalom nem tölthető be');
    const fullHeight = Math.max(Math.ceil(doc.body?.scrollHeight || 0), 200);
    const height = Math.min(fullHeight, MAX_HEIGHT);
    const xhtml = new XMLSerializer().serializeToString(doc.documentElement);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${PAGE_WIDTH}" height="${height}">`
      + `<foreignObject x="0" y="0" width="100%" height="100%">${xhtml}</foreignObject></svg>`;
    const img = new Image();
    img.decoding = 'sync';
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    await img.decode();
    const scale = height * 2 <= 30000 ? 2 : 1;
    const canvas = document.createElement('canvas');
    canvas.width = PAGE_WIDTH * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('A böngésző nem tud képet készíteni');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0);
    return { canvas, truncated: fullHeight > MAX_HEIGHT };
  } finally {
    frame.remove();
  }
}

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob>((resolve, reject) => {
    try {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('A kép nem készíthető el'))), type, quality);
    } catch {
      // pl. Safari: a foreignObject-es kép „szennyezi” a canvast
      reject(new Error('Ez a böngésző nem engedi a kép elkészítését – használj Chrome-ot vagy Edge-et, vagy mentsd HTML-ként.'));
    }
  });

export async function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return canvasToBlob(canvas, 'image/png');
}

/** A4-es, álló PDF: a kép oldalakra vágva. */
export async function canvasToPdf(canvas: HTMLCanvasElement): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageW = 210; const pageH = 297; const margin = 8;
  const usableW = pageW - margin * 2; const usableH = pageH - margin * 2;
  const pxPerMm = canvas.width / usableW;
  const slicePx = Math.floor(usableH * pxPerMm);
  for (let y = 0, page = 0; y < canvas.height; y += slicePx, page++) {
    const h = Math.min(slicePx, canvas.height - y);
    const part = document.createElement('canvas');
    part.width = canvas.width; part.height = h;
    const pctx = part.getContext('2d');
    if (!pctx) throw new Error('A PDF nem készíthető el');
    pctx.fillStyle = '#ffffff'; pctx.fillRect(0, 0, part.width, part.height);
    pctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
    let dataUrl: string;
    try { dataUrl = part.toDataURL('image/jpeg', 0.88); } catch {
      throw new Error('Ez a böngésző nem engedi a PDF elkészítését – használj Chrome-ot vagy Edge-et, vagy mentsd HTML-ként.');
    }
    if (page > 0) pdf.addPage();
    pdf.addImage(dataUrl, 'JPEG', margin, margin, usableW, h / pxPerMm);
  }
  return pdf.output('blob');
}

/** Biztonságos fájlnév-alap (kiterjesztés nélkül). */
export function captureBaseName(name: string) {
  const base = String(name || 'weboldal').replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[\\/:*?"<>|\x00-\x1f]+/g, ' ').replace(/\s+/g, ' ').trim();
  return (base || 'weboldal').slice(0, 120);
}
